import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Landmark, CheckCircle2, XCircle, Clock, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => parseFloat(n || 0).toFixed(2).replace(".", ",");
const STATUS_LABEL = { requested: "Pendente", paid: "Pago", rejected: "Rejeitado" };
const STATUS_CLS = {
  requested: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
};

export default function AdminPayouts() {
  const [filter, setFilter] = useState("requested");
  const [items, setItems] = useState([]);
  const [acting, setActing] = useState(null); // {payout, action}
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/payouts/admin/list${q}`).then((r) => setItems(r.data || []));
  };
  useEffect(() => { refresh(); }, [filter]);

  const openAction = (payout, action) => {
    setActing({ payout, action });
    setReference(""); setNote("");
  };

  const submit = async () => {
    setBusy(true);
    try {
      const url = `/payouts/admin/${acting.payout.id}/${acting.action}`;
      await api.post(url, { external_reference: reference || null, note: note || null });
      toast.success(acting.action === "approve" ? "Marcado como pago" : "Resgate rejeitado e saldo estornado");
      setActing(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-payouts">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-blue-950 flex items-center gap-2 mb-1">
        <Landmark size={28}/>Resgates PIX
      </h1>
      <p className="text-stone-500 text-sm mb-6">As empresas solicitam resgate do saldo BemCash via PIX. Transfira manualmente pelo app Mercado Pago para a chave PIX informada e marque como pago.</p>

      <div className="flex gap-2 mb-4">
        {[
          {k: "requested", label: "Pendentes"},
          {k: "paid", label: "Pagos"},
          {k: "rejected", label: "Rejeitados"},
          {k: "all", label: "Todos"},
        ].map((b) => (
          <Button key={b.k} size="sm" variant={filter === b.k ? "default" : "outline"} onClick={() => setFilter(b.k)} className="rounded-xl" data-testid={`filter-${b.k}`}>
            {b.label}
          </Button>
        ))}
      </div>

      <Card className="rounded-2xl border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Empresa</th>
                <th className="text-left px-4 py-3">Valor</th>
                <th className="text-left px-4 py-3">Chave PIX</th>
                <th className="text-left px-4 py-3">Solicitado em</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-stone-100" data-testid={`payout-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium">{p.company_name}</td>
                  <td className="px-4 py-3 font-display font-bold">B$ {fmt(p.amount)}</td>
                  <td className="px-4 py-3">
                    <code className="font-mono text-xs">{p.pix_key}</code>
                    <button onClick={() => { navigator.clipboard.writeText(p.pix_key); toast.success("Copiado!"); }} className="ml-1 text-stone-400 hover:text-stone-700" title="Copiar"><Copy size={12} className="inline"/></button>
                  </td>
                  <td className="px-4 py-3 text-xs text-stone-500">{new Date(p.created_at).toLocaleString("pt-BR")}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${STATUS_CLS[p.status]}`}>{STATUS_LABEL[p.status]}</span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {p.status === "requested" && (
                      <>
                        <Button size="sm" onClick={() => openAction(p, "approve")} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg" data-testid={`approve-${p.id}`}>
                          <CheckCircle2 size={12} className="mr-1"/>Marcar pago
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openAction(p, "reject")} className="text-red-700 hover:bg-red-50 rounded-lg" data-testid={`reject-${p.id}`}>
                          <XCircle size={12} className="mr-1"/>Rejeitar
                        </Button>
                      </>
                    )}
                    {p.status !== "requested" && p.external_reference && (
                      <code className="text-[10px] text-stone-500">{p.external_reference}</code>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-stone-400 text-sm">Nenhum resgate {filter !== "all" && `(${STATUS_LABEL[filter] || filter})`}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {acting && (
        <Dialog open onOpenChange={(v) => !v && setActing(null)}>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">{acting.action === "approve" ? "Marcar como pago" : "Rejeitar resgate"}</DialogTitle>
            </DialogHeader>
            <div className="p-3 rounded-xl bg-stone-50 border border-stone-200 text-sm space-y-1">
              <div><strong>{acting.payout.company_name}</strong> — B$ {fmt(acting.payout.amount)}</div>
              <div className="text-xs">PIX: <code className="font-mono">{acting.payout.pix_key}</code></div>
            </div>
            {acting.action === "approve" && (
              <>
                <p className="text-xs text-stone-600 mt-2">
                  Antes de marcar como pago, transfira <strong>B$ {fmt(acting.payout.amount)}</strong> pelo app Mercado Pago para a chave PIX acima e anote o comprovante.
                  <a href="https://www.mercadopago.com.br" target="_blank" rel="noopener noreferrer" className="text-blue-700 inline-flex items-center gap-1 ml-1"><ExternalLink size={11}/>Abrir Mercado Pago</a>
                </p>
                <div className="mt-2"><Label>ID do comprovante (opcional)</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} className="rounded-xl mt-1 font-mono text-xs" placeholder="E32B0..." data-testid="payout-ref"/></div>
              </>
            )}
            <div className="mt-2"><Label>Observação (opcional)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="rounded-xl mt-1 text-sm" rows={2}/></div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setActing(null)} className="rounded-xl">Cancelar</Button>
              <Button onClick={submit} disabled={busy} className={`rounded-xl text-white ${acting.action === "approve" ? "bg-emerald-700 hover:bg-emerald-800" : "bg-red-700 hover:bg-red-800"}`} data-testid="payout-confirm-action">
                {busy ? "..." : (acting.action === "approve" ? "Confirmar pagamento" : "Rejeitar e estornar")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
